/*
  GWP gift reveal — a mini-cart progress strip for one or more gift tiers. Shows each gift's
  progress in the cart drawer ("Spend $X more", "unlocked!", "added ✓") with a manual Add button
  for unlocked-but-missing gifts. No celebratory popup, and no automatic detection/auto-add: the
  gift apps (BOGOS / Free Gifts, AutoCart) own attaching gifts; this only reflects state and lets
  the shopper click Add. Those apps re-run their gift rules only when a real product is added to the
  cart, so the Add button triggers that — it bumps a cart line by +1 and restores it — which makes
  the apps re-add every qualifying gift. See nudgeGiftApps() for why other triggers don't work.

  Config comes from sections/gwp-modal.liquid as <script type="application/json" id="GwpGiftRevealConfig">
  with a `gifts` array (one per "Gift reveal" block). Off unless enabled with at least one block.
*/
(function () {
  if (window.__gwpGiftRevealInit) return; // guard against the script being included twice
  window.__gwpGiftRevealInit = true;

  const el = document.getElementById('GwpGiftRevealConfig');
  if (!el) return;

  let cfg;
  try { cfg = JSON.parse(el.textContent); } catch (e) { return; }
  if (!cfg || !cfg.enabled || !Array.isArray(cfg.gifts) || !cfg.gifts.length) return;

  const GIFTS = cfg.gifts
    .filter((g) => g && g.variantId)
    .map((g) => ({
      ...g,
      variantId: Number(g.variantId),
      thresholdCents: Math.max(0, Math.round((Number(g.threshold) || 0) * 100)),
    }));
  if (!GIFTS.length) return;

  // --- cart helpers ---------------------------------------------------------
  async function freshCart() {
    if (typeof window.getCart === 'function') {
      try { return await window.getCart(true, 'gwp-gift-reveal'); } catch (e) {}
    }
    try { return await (await fetch('/cart.js')).json(); } catch (e) { return null; }
  }

  const isGiftCard = (i) => i.gift_card === true || /gift card/i.test(i.product_title || i.title || '');
  const isFreeLine = (i) => ['Free', 'Free Gift', 'Credo Rewards'].includes(i.vendor || '');
  const inCart = (cart, id) => !!cart && (cart.items || []).some((i) => Number(i.variant_id) === Number(id));
  const qtyOf = (cart, id) => ((cart && (cart.items || []).find((i) => Number(i.variant_id) === Number(id))) || {}).quantity || 0;

  // Mirror the BOGOS rule: total value of "all brands minus gift cards" (post-line-discount).
  function qualifyingSubtotalCents(cart) {
    return (cart.items || []).reduce((sum, i) => {
      if (isFreeLine(i) || isGiftCard(i)) return sum;
      const line = Number(i.final_line_price ?? i.line_price ?? Number(i.price) * Number(i.quantity) ?? 0);
      return sum + line;
    }, 0);
  }

  // We do NOT add gifts ourselves — the gift apps (BOGOS / Free Gifts, AutoCart) own them, add each
  // gift free with its own discount, and BLOCK any direct /cart/add.js of a gift variant. The catch:
  // both apps re-run their gift rules ONLY as a side-effect of a real product being added to the
  // cart — they hook /cart/add.js via a fetch-override. Verified on Dev/QA that everything else is a
  // dead end: they ignore /cart/update.js, ignore the `handle-autocart` DOM event, and ignore
  // EasyGift.RefreshCart(). So to make them re-add a missing-but-unlocked gift, we trigger a real
  // add.js they observe: bump a genuine (non-gift) cart line by +1, wait for the apps to re-add
  // every qualifying gift, then restore that line's quantity. Restoring via change.js does NOT
  // remove the gifts — the apps only act on add.js. Returns true if a bump was performed.
  async function nudgeGiftApps() {
    const cart = await freshCart();
    if (!cart) return false;
    // A real product line we can safely bump: not a free/gift line, not a gift card.
    const line = (cart.items || []).find((i) => Number(i.variant_id) && !isFreeLine(i) && !isGiftCard(i));
    if (!line) return false; // cart is only gifts — nothing to trigger the apps with
    const vid = Number(line.variant_id);
    const restoreKey = line.key;
    const original = Number(line.quantity) || 1;
    let bumped = false;
    try {
      await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: vid, quantity: 1 }] }),
      });
      bumped = true;
      await new Promise((r) => setTimeout(r, 1500)); // let the gift apps re-run their rules
    } catch (e) {
      // add.js failed — nothing was bumped, so there is nothing to restore.
    } finally {
      // ALWAYS restore the bumped line so a thrown/aborted operation can never leave the extra unit
      // in the cart. We also request the cart-drawer sections here and repaint via the drawer's own
      // render path — raw fetch()es don't trigger the theme's re-render, and the drawer's cartUpdate
      // subscriber only opens/focuses, so without this the added gift wouldn't show until next nav.
      if (bumped) {
        const drawer = document.querySelector('cart-drawer');
        const sectionIds =
          drawer && typeof drawer.getSectionsToRender === 'function'
            ? drawer.getSectionsToRender().map((s) => s.id)
            : [];
        const body = JSON.stringify({
          id: restoreKey,
          quantity: original,
          sections: sectionIds.join(','),
          sections_url: window.location.pathname,
        });
        for (let attempt = 0; attempt < 2; attempt++) {
          try {
            const res = await fetch('/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body,
            });
            const parsed = await res.json();
            if (drawer && typeof drawer.renderContents === 'function' && parsed && parsed.sections) {
              drawer.renderContents(parsed);
            }
            break; // restored
          } catch (e) {
            if (attempt === 0) await new Promise((r) => setTimeout(r, 600)); // retry once
          }
        }
      }
    }
    return true;
  }

  function publishCartUpdate(cart) {
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'gwp-gift-reveal', cartData: cart });
    }
  }

  // --- mini-cart progress strip ---------------------------------------------
  function money(cents) {
    const n = (Number(cents) || 0) / 100;
    return '$' + (n % 1 === 0 ? n.toFixed(0) : n.toFixed(2));
  }

  function giftRow(cart, g) {
    const name = g.title || 'free gift';
    const img = g.image ? `<img class="gwp-gp__img" src="${g.image}" alt="" loading="lazy">` : '';
    if (inCart(cart, g.variantId)) {
      return `<div class="gwp-gp__row gwp-gp__row--done">${img}
        <span class="gwp-gp__text"><strong>${name}</strong> added</span>
        <span class="gwp-gp__check" aria-hidden="true">✓</span></div>`;
    }
    // Mid-add: keep the row a DISABLED "Adding…" through every re-render, so the button never
    // flickers back to pressable while the bump-restore runs and can't be clicked again.
    if (pendingGiftAdds.has(g.variantId)) {
      return `<div class="gwp-gp__row gwp-gp__row--ready">${img}
        <span class="gwp-gp__text">Adding <strong>${name}</strong>…</span>
        <button type="button" class="gwp-gp__add" data-gift-variant="${g.variantId}" disabled>Adding…</button></div>`;
    }
    // Out of stock (tracked + unavailable at render): don't show the gift at all — no Add button,
    // no progress row. Untracked variants are always purchasable, so they're never hidden here.
    if (g.inventoryTracked && !g.availableAtRender) {
      return '';
    }
    const sub = qualifyingSubtotalCents(cart);
    if (sub >= g.thresholdCents) {
      return `<div class="gwp-gp__row gwp-gp__row--ready">${img}
        <span class="gwp-gp__text">You've unlocked <strong>${name}</strong>!</span>
        <button type="button" class="gwp-gp__add" data-gift-variant="${g.variantId}">Add</button></div>`;
    }
    const remain = g.thresholdCents - sub;
    const pct = Math.max(0, Math.min(100, Math.round((100 * sub) / g.thresholdCents)));
    return `<div class="gwp-gp__row gwp-gp__row--progress">
      <span class="gwp-gp__text">Spend ${money(remain)} more to unlock <strong>${name}</strong></span>
      <span class="gwp-gp__bar"><span class="gwp-gp__bar-fill" style="width:${pct}%"></span></span></div>`;
  }

  // Gift variants a click is currently adding. giftRow renders these as a disabled "Adding…", so
  // every re-render (MutationObserver, cartUpdate, handler) keeps the row locked — no pressable
  // flicker mid-operation and no concurrent/double fire.
  const pendingGiftAdds = new Set();
  let lastCart = null;

  function renderProgress(cart) {
    if (cart) lastCart = cart;
    const mounts = document.querySelectorAll('[data-gwp-gift-progress]');
    if (!mounts.length || !cart) return;
    injectStyles();
    const hasMerch = (cart.items || []).some((i) => !isFreeLine(i) && !isGiftCard(i) && Number(i.final_line_price ?? Number(i.price) * Number(i.quantity) ?? 0) > 0);
    // Any-purchase gifts (threshold 0) only make sense once there's merchandise; tiered gifts always show.
    const rows = (cart.items || []).length && hasMerch
      ? GIFTS.filter((g) => g.thresholdCents > 0 || hasMerch).map((g) => giftRow(cart, g)).join('')
      : '';
    mounts.forEach((m) => {
      m.innerHTML = rows;
      m.hidden = !rows;
    });
  }

  function injectStyles() {
    if (document.getElementById('GwpGiftRevealStyles')) return;
    const s = document.createElement('style');
    s.id = 'GwpGiftRevealStyles';
    s.textContent = `
      .gwp-gift-progress{display:flex;flex-direction:column;gap:8px;padding:12px 16px;
        background:#f4f8f9;border-bottom:1px solid #e3ebed}
      .gwp-gp__row{display:flex;align-items:center;gap:10px;font-size:13px;color:#001922}
      .gwp-gp__img{width:34px;height:34px;object-fit:contain;flex:0 0 auto}
      .gwp-gp__text{flex:1 1 auto;line-height:1.3}
      .gwp-gp__check{color:#0B788E;font-weight:700}
      .gwp-gp__row--oos .gwp-gp__text{color:#8a9499}
      .gwp-gp__add{flex:0 0 auto;padding:6px 14px;border:0;border-radius:4px;cursor:pointer;
        background:#0B788E;color:#fff;font-size:13px;font-weight:600}
      .gwp-gp__add[disabled]{opacity:.6;cursor:default}
      .gwp-gp__bar{display:block;flex:1 1 100%;height:6px;border-radius:3px;background:#dfe8ea;overflow:hidden}
      .gwp-gp__bar-fill{display:block;height:100%;background:#0B788E;transition:width .3s ease}
      .gwp-gp__row--progress{flex-wrap:wrap}`;
    document.head.appendChild(s);
  }

  // --- triggers -------------------------------------------------------------
  function start() {
    freshCart().then(renderProgress); // paint the mini-cart strip immediately

    // Add button inside the mini-cart strip — nudges the gift apps to re-run their rules and add it.
    document.addEventListener('click', async (e) => {
      const btn = e.target.closest && e.target.closest('.gwp-gp__add');
      if (!btn || btn.disabled) return; // ignore an in-flight (disabled) button
      e.preventDefault();
      const g = GIFTS.find((x) => x.variantId === Number(btn.getAttribute('data-gift-variant')));
      if (!g || pendingGiftAdds.has(g.variantId)) return; // already adding this gift — no double fire
      btn.disabled = true;
      btn.textContent = 'Adding…';
      pendingGiftAdds.add(g.variantId);
      renderProgress(lastCart); // lock every copy of the row to a disabled "Adding…"
      try {
        // Guard: if the gift is already in the cart (e.g. a stale button), don't trigger a needless
        // bump — just fall through to the finally, which repaints the row to "added ✓".
        const current = await freshCart();
        if (current && inCart(current, g.variantId)) return;
        await nudgeGiftApps();
        await new Promise((r) => setTimeout(r, 1500));
      } finally {
        // Always release the lock and settle to the real state — "added ✓" if it worked, or a fresh
        // pressable "Add" if it didn't (retryable). No permanent stuck/"Try again" state.
        pendingGiftAdds.delete(g.variantId);
        const cart = await freshCart();
        publishCartUpdate(cart);
        renderProgress(cart);
      }
    });

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (evt) => {
        if (evt && evt.cartData) renderProgress(evt.cartData);
        else freshCart().then(renderProgress);
      });
    }

    // The drawer re-renders by replacing #CartDrawer / .drawer__inner, which wipes our mount.
    const drawer = document.querySelector('cart-drawer') || document.getElementById('CartDrawer');
    if (drawer && 'MutationObserver' in window) {
      let scheduled = false;
      new MutationObserver(() => {
        if (scheduled) return;
        const m = document.querySelector('[data-gwp-gift-progress]');
        if (m && !m.innerHTML && lastCart) {
          scheduled = true;
          requestAnimationFrame(() => { scheduled = false; renderProgress(lastCart); });
        }
      }).observe(drawer, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
